import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Device } from '@twilio/voice-sdk';
import { phoneNumbersAPI, customersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Phone, PhoneOff, PhoneIncoming, PhoneOutgoing,
  Mic, MicOff, Loader2, Delete, ExternalLink,
  Grid3x3, X, Pause, Play, UserPlus, GripHorizontal,
  Search, Clock, Star, ArrowUpRight, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { formatTimestampCompact } from '../lib/timezone';

// Dialpad keys with sub-labels
const DIALPAD_KEYS = [
  { digit: '1', letters: '' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' },
  { digit: '0', letters: '+' },
  { digit: '#', letters: '' },
];

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatPhoneDisplay(number) {
  if (!number) return '';
  const d = number.replace(/[^\d]/g, '');
  if (d.length === 11 && d[0] === '1') {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return number;
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0][0]?.toUpperCase() || '?';
}

// Format phone number as user types for display
function formatPhoneLive(raw) {
  const d = raw.replace(/[^\d+*#]/g, '');
  // Only format pure digit strings (US numbers)
  const digits = d.replace(/[^\d]/g, '');
  if (d !== digits) return d; // has +, *, # — leave raw
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1')
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return d;
}

// Simple drag hook for the dialer panel
function useDrag() {
  const [pos, setPos] = useState(null); // null = use default CSS position
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const panelRef = useRef(null);

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    offset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    dragging.current = true;
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const x = Math.max(0, Math.min(window.innerWidth - 380, e.clientX - offset.current.x));
      const y = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - offset.current.y));
      setPos({ x, y });
    };
    const onMouseUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // Reset position when panel is closed and reopened
  const resetPos = useCallback(() => setPos(null), []);

  return { pos, panelRef, onMouseDown, resetPos };
}

// ────────────────────────────────────────────────
// Contact Avatar
// ────────────────────────────────────────────────
function ContactAvatar({ name, size = 'lg' }) {
  const sizes = {
    sm: 'w-10 h-10 text-sm',
    md: 'w-14 h-14 text-lg',
    lg: 'w-20 h-20 text-2xl',
    xl: 'w-24 h-24 text-3xl',
  };
  return (
    <div className={cn(
      'rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center flex-shrink-0 shadow-sm ring-1 ring-primary/10',
      sizes[size]
    )}>
      {getInitials(name)}
    </div>
  );
}

// ────────────────────────────────────────────────
// Dialpad Grid (with letters)
// ────────────────────────────────────────────────
function DialpadGrid({ onKey }) {
  return (
    <div className="grid grid-cols-3 gap-3 px-6">
      {DIALPAD_KEYS.map(({ digit, letters }) => (
        <button
          key={digit}
          onClick={() => onKey(digit)}
          className={cn(
            'flex flex-col items-center justify-center rounded-full transition-all duration-150',
            'h-[68px] w-[68px] mx-auto',
            'bg-muted/30 hover:bg-muted/60 active:scale-[0.92]',
            'shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)]',
            'select-none cursor-pointer'
          )}
          data-testid={`dialer-key-${digit}`}
        >
          <span className="text-[22px] font-semibold leading-none">{digit}</span>
          {letters && (
            <span className="text-[8px] tracking-[0.2em] text-muted-foreground/60 mt-1 leading-none font-medium">
              {letters}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────
// Call Control Button
// ────────────────────────────────────────────────
function CallControl({ icon: Icon, label, active, destructive, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 group"
      data-testid={testId}
    >
      <div className={cn(
        'w-12 h-12 rounded-full flex items-center justify-center transition-all duration-150 active:scale-[0.92]',
        destructive && 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm',
        active && !destructive && 'bg-primary text-primary-foreground shadow-md shadow-primary/20',
        !active && !destructive && 'bg-muted/60 hover:bg-muted text-foreground/70 hover:text-foreground shadow-sm',
      )}>
        <Icon className="w-5 h-5" />
      </div>
      {label && <span className="text-[10px] text-muted-foreground group-hover:text-foreground/80 transition-colors">{label}</span>}
    </button>
  );
}

// ────────────────────────────────────────────────
// Bar Control Button (compact, for bottom call bar)
// ────────────────────────────────────────────────
function BarControl({ icon: Icon, label, active, disabled, onClick, testId }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      className={cn(
        'flex flex-col items-center gap-0.5 group transition-all',
        disabled && 'opacity-25 cursor-not-allowed',
      )}
      data-testid={testId}
      disabled={disabled}
    >
      <div className={cn(
        'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 active:scale-[0.92]',
        active ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20'
               : 'bg-muted/40 hover:bg-muted text-foreground/60 hover:text-foreground',
      )}>
        <Icon className="w-[15px] h-[15px]" />
      </div>
      {label && <span className="text-[8px] leading-tight text-muted-foreground/70 group-hover:text-foreground/60 transition-colors">{label}</span>}
    </button>
  );
}

// ────────────────────────────────────────────────
// Contact Row (for search results & frequent)
// ────────────────────────────────────────────────
function ContactRow({ name, phone, subtitle, onCall, testId }) {
  const display = name || formatPhoneDisplay(phone || '');
  const initials = name
    ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '#';
  return (
    <button
      onClick={() => onCall(phone || '')}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/50 active:bg-accent/70 transition-all duration-150 cursor-pointer text-left group border-b border-border/15 last:border-b-0 hover:shadow-[inset_0_0_0_1px_rgba(0,0,0,0.03)]"
      data-testid={testId}
    >
      <div className="w-9 h-9 rounded-full bg-muted group-hover:bg-muted-foreground/10 flex items-center justify-center flex-shrink-0 transition-colors">
        <span className="text-[11px] font-semibold text-muted-foreground">{initials}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium truncate leading-tight">{display}</p>
        {name && phone && (
          <p className="text-[11px] text-muted-foreground/60 truncate leading-tight mt-0.5">{formatPhoneDisplay(phone)}</p>
        )}
        {subtitle && (
          <p className="text-[10px] text-muted-foreground/50 leading-tight mt-0.5">{subtitle}</p>
        )}
      </div>
      <Phone className="w-4 h-4 text-muted-foreground/20 group-hover:text-primary/70 transition-colors flex-shrink-0" />
    </button>
  );
}

// ────────────────────────────────────────────────
// Recent Call Row
// ────────────────────────────────────────────────
function RecentCallRow({ name, phone, direction, status, timestamp, duration, onCall, testId }) {
  const display = name || formatPhoneDisplay(phone || '');
  const isMissed = status === 'missed' || status === 'no-answer';
  const DirIcon = direction === 'inbound' ? PhoneIncoming : PhoneOutgoing;
  const initials = name
    ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '#';

  const fmtDuration = (s) => {
    if (!s || s <= 0) return '';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  return (
    <button
      onClick={() => onCall(phone || '')}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/50 active:bg-accent/70 transition-all duration-150 cursor-pointer text-left group border-b border-border/15 last:border-b-0 hover:shadow-[inset_0_0_0_1px_rgba(0,0,0,0.03)]"
      data-testid={testId}
    >
      <div className="relative flex-shrink-0">
        <div className="w-9 h-9 rounded-full bg-muted group-hover:bg-muted-foreground/10 flex items-center justify-center transition-colors">
          <span className="text-[11px] font-semibold text-muted-foreground">{initials}</span>
        </div>
        <DirIcon className={cn(
          'w-3 h-3 absolute -bottom-0.5 -right-0.5',
          isMissed ? 'text-destructive' : 'text-muted-foreground/50',
        )} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn(
          'text-[13px] font-medium truncate leading-tight',
          isMissed && 'text-destructive',
        )}>{display}</p>
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground/50 leading-tight mt-0.5">
          {timestamp && <span>{formatTimestampCompact(timestamp)}</span>}
          {duration > 0 && <><span>·</span><span>{fmtDuration(duration)}</span></>}
        </div>
      </div>
      <Phone className="w-4 h-4 text-muted-foreground/20 group-hover:text-primary/70 transition-colors flex-shrink-0" />
    </button>
  );
}

// ════════════════════════════════════════════════
// Main Dialer (Softphone) Component
// ════════════════════════════════════════════════
export function Softphone() {
  const navigate = useNavigate();

  // ── Dialer open/close state ──
  const [isOpen, setIsOpen] = useState(false);

  // ── Twilio state (preserved exactly from original) ──
  const [device, setDevice] = useState(null);
  const [deviceReady, setDeviceReady] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [callState, setCallState] = useState('idle'); // idle | dialing | ringing | connected | incoming
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [dialNumber, setDialNumber] = useState('');
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callerInfo, setCallerInfo] = useState('');
  const [callerIdNumber, setCallerIdNumber] = useState('');
  const [matchedCustomer, setMatchedCustomer] = useState(null);
  const [showKeypadInCall, setShowKeypadInCall] = useState(false);
  const [tenantNumbers, setTenantNumbers] = useState([]); // all voice-enabled numbers for "Dial From"
  const callerIdRef = useRef('');
  const durationTimer = useRef(null);
  const deviceRef = useRef(null);
  const callLogIdRef = useRef(null);
  const inputRef = useRef(null);
  const callDurationRef = useRef(0);
  const callStateRef = useRef('idle');

  // ── Panel tabs state ──
  const [panelTab, setPanelTab] = useState('dialer'); // dialer | recent | frequent
  const [recentCalls, setRecentCalls] = useState([]);
  const [frequentContacts, setFrequentContacts] = useState([]);
  const [panelSearch, setPanelSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [loadingFrequent, setLoadingFrequent] = useState(false);

  // ── Drag support ──
  const { pos, panelRef, onMouseDown: onDragStart, resetPos } = useDrag();

  // ── Customer lookup (unchanged) ──
  const lookupCustomer = useCallback(async (phone) => {
    if (!phone) return null;
    try {
      const res = await phoneNumbersAPI.lookupCustomer(phone);
      const data = res.data;
      if (data.customer_id) {
        return { id: data.customer_id, name: data.customer_name };
      }
    } catch (e) {
      console.error('Customer lookup error:', e);
    }
    return null;
  }, []);

  // ── Fetch recent calls when tab switches ──
  const fetchRecent = useCallback(async () => {
    setLoadingRecent(true);
    try {
      const res = await phoneNumbersAPI.globalCalls({ filter: 'all', page: 1, page_size: 20 });
      setRecentCalls(res.data?.calls || []);
    } catch (e) { console.error('Failed to fetch recent calls:', e); }
    setLoadingRecent(false);
  }, []);

  const fetchFrequent = useCallback(async () => {
    setLoadingFrequent(true);
    try {
      const res = await phoneNumbersAPI.frequentContacts(15);
      setFrequentContacts(res.data?.contacts || []);
    } catch (e) { console.error('Failed to fetch frequent contacts:', e); }
    setLoadingFrequent(false);
  }, []);

  useEffect(() => {
    if (!isOpen || callState !== 'idle') return;
    if (panelTab === 'recent') fetchRecent();
    else if (panelTab === 'frequent') fetchFrequent();
  }, [panelTab, isOpen, callState, fetchRecent, fetchFrequent]);

  // ── Contact search (debounced) ──
  const searchTimer = useRef(null);
  useEffect(() => {
    if (!panelSearch.trim()) { setSearchResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await customersAPI.list({ search: panelSearch.trim(), page: 1, page_size: 10 });
        const customers = res.data?.customers || res.data || [];
        setSearchResults(customers);
      } catch (e) { console.error('Contact search error:', e); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [panelSearch]);

  // ── Initialize Twilio Device (unchanged logic) ──
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const res = await phoneNumbersAPI.list();
        const numbers = res.data || [];
        const voiceNumbers = numbers.filter(n => n.voice_enabled && n.is_active);
        setTenantNumbers(voiceNumbers);
        const voiceNumber = voiceNumbers.find(n => n.is_primary) || voiceNumbers[0];
        if (voiceNumber) {
          setCallerIdNumber(voiceNumber.phone_number);
          callerIdRef.current = voiceNumber.phone_number;
        }

        const tokenRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        });

        if (!tokenRes.ok) {
          const err = await tokenRes.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to get voice token');
        }

        if (cancelled) return;
        const { token } = await tokenRes.json();

        const newDevice = new Device(token, {
          logLevel: 1,
          codecPreferences: ['opus', 'pcmu'],
        });

        newDevice.on('registered', () => {
          if (!cancelled) { setDeviceReady(true); setRegistering(false); }
        });

        newDevice.on('unregistered', () => {
          if (!cancelled) setDeviceReady(false);
        });

        newDevice.on('error', (err) => {
          console.error('Twilio Device error:', err);
          if (!cancelled) toast.error(`Phone error: ${err.message || 'Unknown'}`);
        });

        newDevice.on('incoming', async (call) => {
          if (cancelled) return;
          const fromNumber = call.parameters?.From || '';
          setIncomingCall(call);
          setCallerInfo(fromNumber);
          setCallState('incoming');
          setIsOpen(true); // Auto-open dialer on incoming call

          const cust = await lookupCustomer(fromNumber);
          if (!cancelled) setMatchedCustomer(cust);

          call.on('cancel', () => {
            setIncomingCall(null);
            setCallState('idle');
            setCallerInfo('');
            setMatchedCustomer(null);
          });

          call.on('disconnect', () => {
            stopDurationTimer();
            setActiveCall(null);
            setIncomingCall(null);
            setCallState('idle');
            setMuted(false);
            setHeld(false);
            setCallerInfo('');
            setMatchedCustomer(null);
          });
        });

        newDevice.on('tokenWillExpire', async () => {
          try {
            const refreshRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/token`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'Content-Type': 'application/json',
              },
            });
            if (refreshRes.ok) {
              const { token: newToken } = await refreshRes.json();
              newDevice.updateToken(newToken);
            }
          } catch (e) {
            console.error('Token refresh failed:', e);
          }
        });

        await newDevice.register();
        if (cancelled) { newDevice.destroy(); return; }
        deviceRef.current = newDevice;
        setDevice(newDevice);
      } catch (err) {
        console.error('Dialer init error:', err);
        if (!cancelled) {
          setRegistering(false);
          if (err.message?.includes('not configured')) {
            toast.error('Twilio credentials not configured');
          }
        }
      }
    }

    setRegistering(true);
    init();

    return () => {
      cancelled = true;
      if (durationTimer.current) clearInterval(durationTimer.current);
      if (deviceRef.current) { deviceRef.current.destroy(); deviceRef.current = null; }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Listen for dialer toggle and click-to-call events ──
  useEffect(() => {
    const toggleHandler = () => setIsOpen(prev => !prev);
    const dialHandler = (e) => {
      const { number, customerId, customerName } = e.detail || {};
      if (!number) return;
      setDialNumber(number);
      if (customerId) setMatchedCustomer({ id: customerId, name: customerName || null });
      setIsOpen(true);
      if (deviceRef.current && deviceReady) {
        setTimeout(() => {
          document.querySelector('[data-testid="dialer-call-btn"]')?.click();
        }, 200);
      }
    };

    window.addEventListener('softphone:toggle', toggleHandler);
    window.addEventListener('softphone:dial', dialHandler);
    return () => {
      window.removeEventListener('softphone:toggle', toggleHandler);
      window.removeEventListener('softphone:dial', dialHandler);
    };
  }, [deviceReady]);

  // ── Broadcast device ready state for the header icon ──
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('softphone:status', { detail: { ready: deviceReady, registering } }));
  }, [deviceReady, registering]);

  // ── Auto-focus input when dialer opens in idle state ──
  useEffect(() => {
    if (isOpen && callState === 'idle') {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (!isOpen) resetPos();
  }, [isOpen, callState, resetPos]);

  // ── Duration timer (unchanged) ──
  const startDurationTimer = () => {
    setCallDuration(0);
    callDurationRef.current = 0;
    durationTimer.current = setInterval(() => {
      callDurationRef.current += 1;
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  const stopDurationTimer = () => {
    if (durationTimer.current) { clearInterval(durationTimer.current); durationTimer.current = null; }
  };

  // ── Call end handler (uses refs to avoid stale closure) ──
  const handleCallEnd = () => {
    stopDurationTimer();
    if (callLogIdRef.current) {
      const finalStatus = callStateRef.current === 'connected' ? 'completed' : 'missed';
      phoneNumbersAPI.callEnd({
        call_log_id: callLogIdRef.current,
        status: finalStatus,
        duration_seconds: callDurationRef.current,
      }).catch(e => console.error('Call log end error:', e));
      callLogIdRef.current = null;
    }
    setActiveCall(null);
    setIncomingCall(null);
    setCallState('idle');
    callStateRef.current = 'idle';
    setMuted(false);
    setHeld(false);
    setCallerInfo('');
    setMatchedCustomer(null);
    setShowKeypadInCall(false);
    callDurationRef.current = 0;
  };

  // ── Make outbound call (unchanged logic) ──
  const makeCall = async () => {
    if (!device || !deviceReady) { toast.error('Phone not ready'); return; }
    if (!dialNumber.trim()) { toast.error('Enter a phone number'); return; }

    setCallState('dialing');
    callStateRef.current = 'dialing';
    setCallerInfo(dialNumber);
    setIsOpen(true);

    if (!matchedCustomer) {
      const cust = await lookupCustomer(dialNumber);
      if (cust) setMatchedCustomer(cust);
    }

    try {
      const call = await device.connect({
        params: { To: dialNumber, callerId: callerIdRef.current || callerIdNumber },
      });
      setActiveCall(call);

      phoneNumbersAPI.callStart({
        direction: 'outbound',
        from_number: callerIdRef.current || callerIdNumber || '',
        to_number: dialNumber,
        twilio_call_sid: call.parameters?.CallSid || null,
      }).then(res => {
        callLogIdRef.current = res.data.call_log_id;
        if (res.data.customer_id) {
          setMatchedCustomer({ id: res.data.customer_id, name: res.data.customer_name });
        }
      }).catch(e => console.error('Call log start error:', e));

      call.on('ringing', () => { setCallState('ringing'); callStateRef.current = 'ringing'; });
      call.on('accept', () => { setCallState('connected'); callStateRef.current = 'connected'; startDurationTimer(); });
      call.on('disconnect', () => handleCallEnd());
      call.on('cancel', () => handleCallEnd());
      call.on('error', (err) => {
        toast.error(`Call failed: ${err.message || 'Unknown error'}`);
        handleCallEnd();
      });
    } catch (err) {
      toast.error(`Call failed: ${err.message || 'Unknown error'}`);
      setCallState('idle');
      setMatchedCustomer(null);
    }
  };

  // ── Accept incoming call ──
  const acceptCall = () => {
    if (!incomingCall) return;
    incomingCall.accept();
    setActiveCall(incomingCall);
    setCallState('connected');
    callStateRef.current = 'connected';
    startDurationTimer();

    phoneNumbersAPI.callStart({
      direction: 'inbound',
      from_number: callerInfo || '',
      to_number: callerIdRef.current || callerIdNumber || '',
      twilio_call_sid: incomingCall.parameters?.CallSid || null,
    }).then(res => {
      callLogIdRef.current = res.data.call_log_id;
      if (res.data.customer_id) {
        setMatchedCustomer({ id: res.data.customer_id, name: res.data.customer_name });
      }
    }).catch(e => console.error('Call log start error:', e));

    incomingCall.on('disconnect', () => handleCallEnd());
  };

  // ── Reject incoming call (unchanged logic) ──
  const rejectCall = () => {
    if (incomingCall) {
      phoneNumbersAPI.callStart({
        direction: 'inbound',
        from_number: callerInfo || '',
        to_number: callerIdRef.current || callerIdNumber || '',
        twilio_call_sid: incomingCall.parameters?.CallSid || null,
      }).then(res => {
        phoneNumbersAPI.callEnd({
          call_log_id: res.data.call_log_id,
          status: 'rejected',
          duration_seconds: 0,
        }).catch(e => console.error('Call log end error:', e));
      }).catch(e => console.error('Call log start error:', e));
      incomingCall.reject();
    }
    setIncomingCall(null);
    setCallState('idle');
    setCallerInfo('');
    setMatchedCustomer(null);
  };

  const hangUp = () => {
    if (activeCall) activeCall.disconnect();
    handleCallEnd();
  };

  const toggleMute = () => {
    if (activeCall) {
      const newMuted = !muted;
      activeCall.mute(newMuted);
      setMuted(newMuted);
    }
  };

  const toggleHold = () => {
    // Hold is simulated via mute for now (Twilio Client SDK doesn't have native hold)
    if (activeCall) {
      const newHeld = !held;
      activeCall.mute(newHeld);
      setHeld(newHeld);
      setMuted(newHeld);
    }
  };

  const handleDialKey = (key) => {
    if (callState === 'connected' && activeCall) {
      activeCall.sendDigits(key);
    } else {
      setDialNumber(prev => prev + key);
    }
  };

  const openCustomerProfile = () => {
    if (matchedCustomer?.id) navigate(`/customers/${matchedCustomer.id}`);
  };

  const isInCall = ['dialing', 'ringing', 'connected', 'incoming'].includes(callState);
  const isActiveCall = ['dialing', 'ringing', 'connected'].includes(callState);
  const displayName = matchedCustomer?.name || null;
  const displayPhone = formatPhoneDisplay(callerInfo || dialNumber);

  // ── Broadcast active call state for layout spacing ──
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('softphone:activecall', { detail: { active: isActiveCall } }));
  }, [isActiveCall]);

  // ════════════════════════════════════════════
  // Render: Active call → bottom bar
  //         Incoming → utility panel prompt
  //         Idle → utility panel dialpad
  // ════════════════════════════════════════════
  return (
    <>
      {/* ── PERSISTENT BOTTOM CALL BAR (active calls) ── */}
      {isActiveCall && (
        <div className="fixed bottom-0 left-0 right-0 z-50" data-testid="active-call-bar-wrapper">
          {/* Keypad popover — anchored above the bar */}
          {showKeypadInCall && (
            <div
              className="absolute bottom-full right-4 mb-2 w-[280px] bg-background border border-border/60 rounded-2xl shadow-[0_-8px_32px_-8px_rgba(0,0,0,0.15)] p-4 animate-in fade-in-0 slide-in-from-bottom-2 duration-200"
              data-testid="call-bar-keypad-popover"
            >
              <div className="grid grid-cols-3 gap-1.5">
                {DIALPAD_KEYS.map(({ digit, letters }) => (
                  <button
                    key={digit}
                    onClick={() => handleDialKey(digit)}
                    className="flex flex-col items-center justify-center rounded-full h-12 w-12 mx-auto bg-muted/30 hover:bg-muted/60 active:scale-[0.92] transition-all select-none"
                    data-testid={`call-bar-key-${digit}`}
                  >
                    <span className="text-base font-semibold leading-none">{digit}</span>
                    {letters && <span className="text-[7px] tracking-[0.15em] text-muted-foreground/50 mt-0.5">{letters}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* The bar */}
          <div
            className={cn(
              'flex items-center gap-4 px-5 py-2 border-t',
              'bg-card/95 backdrop-blur-sm text-card-foreground',
              'border-border/60',
              'shadow-[0_-2px_16px_-2px_rgba(0,0,0,0.08),0_-1px_3px_0_rgba(0,0,0,0.04)]',
            )}
            data-testid="active-call-bar"
          >
            {/* ── LEFT: Contact info ── */}
            <div className="flex items-center gap-2.5 min-w-0 flex-shrink-0">
              <ContactAvatar name={displayName || callerInfo} size="sm" />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  {displayName ? (
                    <p className="text-[13px] font-semibold tracking-tight truncate max-w-[180px]" data-testid="call-bar-name">{displayName}</p>
                  ) : (
                    <p className="text-[13px] font-semibold tracking-tight truncate max-w-[180px]" data-testid="call-bar-phone">{displayPhone}</p>
                  )}
                  {displayName && (
                    <span className="text-[11px] text-muted-foreground/60 hidden sm:inline">{displayPhone}</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 mt-px">
                  {callState === 'connected' ? (
                    <>
                      <span className="text-base font-mono tabular-nums text-primary font-semibold leading-tight" data-testid="call-bar-timer">
                        {formatDuration(callDuration)}
                      </span>
                      <span className="flex items-center gap-1 text-[9px] text-emerald-600/80 dark:text-emerald-400/80 font-medium">
                        <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
                        Live
                      </span>
                    </>
                  ) : (
                    <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {callState === 'dialing' ? 'Connecting...' : 'Ringing...'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* ── CENTER: Controls ── */}
            <div className="flex-1 flex items-center justify-center gap-3 sm:gap-5">
              <BarControl
                icon={muted ? MicOff : Mic}
                label={muted ? 'Unmute' : 'Mute'}
                active={muted}
                onClick={toggleMute}
                testId="call-bar-mute"
              />
              <BarControl
                icon={held ? Play : Pause}
                label={held ? 'Resume' : 'Hold'}
                active={held}
                onClick={toggleHold}
                testId="call-bar-hold"
              />
              <BarControl
                icon={Grid3x3}
                label="Keypad"
                active={showKeypadInCall}
                onClick={() => setShowKeypadInCall(p => !p)}
                testId="call-bar-keypad"
              />
              {matchedCustomer?.id && (
                <BarControl
                  icon={ExternalLink}
                  label="Customer"
                  onClick={openCustomerProfile}
                  testId="call-bar-open-customer"
                />
              )}
              {/* Future placeholders — visible but disabled */}
              <BarControl icon={PhoneOutgoing} label="Transfer" disabled testId="call-bar-transfer" />
            </div>

            {/* ── RIGHT: End Call ── */}
            <button
              onClick={hangUp}
              className="flex-shrink-0 h-9 px-4 rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground flex items-center gap-1.5 shadow-[0_2px_8px_-1px] shadow-destructive/30 hover:shadow-[0_4px_14px_-2px] hover:shadow-destructive/40 transition-all duration-150 active:scale-[0.95]"
              data-testid="call-bar-hangup"
            >
              <PhoneOff className="w-3.5 h-3.5" />
              <span className="text-xs font-semibold hidden sm:inline">End</span>
            </button>
          </div>
        </div>
      )}

      {/* ── UTILITY PANEL (idle dialpad + incoming call) ── */}
      {isOpen && !isActiveCall && (() => {
        const panelStyle = pos
          ? { left: pos.x, top: pos.y, transform: 'none' }
          : undefined;

        return (
          <div
            ref={panelRef}
            style={panelStyle}
            className={cn(
              'fixed z-50 animate-in fade-in-0 slide-in-from-bottom-4 duration-250',
              !pos && 'top-16 right-6',
              'max-sm:!top-auto max-sm:!bottom-0 max-sm:!left-0 max-sm:!right-0 max-sm:!translate-x-0 max-sm:!translate-y-0',
              'w-full max-w-[380px] max-sm:max-w-full',
              'bg-background rounded-2xl max-sm:rounded-b-none',
              'border border-border/60',
              'shadow-[0_24px_80px_-12px_rgba(0,0,0,0.25),0_8px_24px_-4px_rgba(0,0,0,0.12)]',
              'flex flex-col overflow-hidden',
            )}
            data-testid="dialer-panel"
          >
            {/* ── Header (drag handle) ── */}
            <div
              className="flex items-center justify-between px-5 py-3.5 border-b border-border/40 cursor-grab active:cursor-grabbing select-none"
              onMouseDown={onDragStart}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Phone className="w-3.5 h-3.5 text-primary" />
                </div>
                <span className="text-sm font-semibold tracking-tight">Phone</span>
                {registering && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                {deviceReady && (
                  <span className="flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Ready
                  </span>
                )}
                {!deviceReady && !registering && (
                  <span className="flex items-center gap-1 text-[10px] text-destructive font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                    Offline
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <GripHorizontal className="w-4 h-4 text-muted-foreground/40" />
                <button
                  onClick={() => setIsOpen(false)}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="text-muted-foreground/60 hover:text-foreground transition-all p-1.5 rounded-lg hover:bg-muted/60 active:scale-[0.92]"
                  data-testid="dialer-close-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* ── INCOMING CALL VIEW ── */}
            {callState === 'incoming' && (
              <div className="flex flex-col items-center py-10 px-6 gap-4" data-testid="dialer-incoming">
                <ContactAvatar name={displayName || callerInfo} size="lg" />
                <div className="text-center space-y-1">
                  {displayName && (
                    <p className="text-xl font-semibold" data-testid="dialer-customer-name">{displayName}</p>
                  )}
                  <p className={cn('font-medium', displayName ? 'text-sm text-muted-foreground' : 'text-xl')}>
                    {displayPhone}
                  </p>
                  <div className="flex items-center justify-center gap-1.5 mt-2">
                    <PhoneIncoming className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-sm text-muted-foreground">Incoming call</span>
                  </div>
                </div>

                {matchedCustomer?.id && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={openCustomerProfile}
                    className="text-xs gap-1.5"
                    data-testid="dialer-open-customer"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open Customer
                  </Button>
                )}

                <div className="flex items-center gap-6 mt-4">
                  <CallControl
                    icon={PhoneOff}
                    label="Decline"
                    destructive
                    onClick={rejectCall}
                    testId="dialer-reject-call"
                  />
                  <button
                    onClick={acceptCall}
                    className="w-[68px] h-[68px] rounded-full bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center shadow-[0_4px_20px_-2px] shadow-emerald-600/40 hover:shadow-emerald-500/50 transition-all duration-200 hover:scale-[1.04] active:scale-[0.94]"
                    data-testid="dialer-accept-call"
                  >
                    <Phone className="w-7 h-7" />
                  </button>
                </div>
              </div>
            )}

            {/* ── IDLE / TABBED PANEL VIEW ── */}
            {callState === 'idle' && (
              <div className="flex flex-col" data-testid="dialer-panel-idle">
                {/* ── Top utility row ── */}
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/30 bg-muted/20">
                  <button
                    onClick={() => { navigate('/calls'); setIsOpen(false); }}
                    className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-lg hover:bg-muted/70 transition-colors"
                    data-testid="dialer-goto-calls"
                  >
                    <ArrowUpRight className="w-3.5 h-3.5" />
                    Calls
                  </button>
                  <div className="flex-1" />
                  <div className="relative flex items-center">
                    <Search className="absolute left-2.5 w-3.5 h-3.5 text-muted-foreground/40 pointer-events-none" />
                    <input
                      value={panelSearch}
                      onChange={(e) => setPanelSearch(e.target.value)}
                      placeholder="Search contacts..."
                      className="w-full h-8 pl-8 pr-3 text-xs rounded-lg bg-muted/50 border border-border/30 outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/30 placeholder:text-muted-foreground/40 transition-all"
                      data-testid="dialer-search-input"
                    />
                  </div>
                </div>

                {/* ── Search results overlay ── */}
                {panelSearch.trim() && (
                  <div className="max-h-[280px] overflow-y-auto border-b border-border/30" data-testid="dialer-search-results">
                    {searching ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : searchResults.length === 0 ? (
                      <p className="text-center text-xs text-muted-foreground/60 py-6">No contacts found</p>
                    ) : (
                      searchResults.map(c => (
                        <ContactRow
                          key={c.id}
                          name={c.display_name}
                          phone={c.primary_phone}
                          onCall={(num) => { setDialNumber(num.replace(/[^\d+]/g, '')); setPanelSearch(''); setPanelTab('dialer'); }}
                          testId={`search-result-${c.id}`}
                        />
                      ))
                    )}
                  </div>
                )}

                {/* ── Tabs (only show when not searching) ── */}
                {!panelSearch.trim() && (
                  <>
                    <div className="flex border-b border-border/30" data-testid="dialer-tabs">
                      {[
                        { key: 'dialer', label: 'Dialer', icon: Grid3x3 },
                        { key: 'recent', label: 'Recent', icon: Clock },
                        { key: 'frequent', label: 'Frequent', icon: Star },
                      ].map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => setPanelTab(tab.key)}
                          className={cn(
                            'flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-medium transition-colors relative',
                            panelTab === tab.key
                              ? 'text-foreground'
                              : 'text-muted-foreground/60 hover:text-muted-foreground',
                          )}
                          data-testid={`dialer-tab-${tab.key}`}
                        >
                          <tab.icon className="w-3 h-3" />
                          {tab.label}
                          {panelTab === tab.key && (
                            <span className="absolute bottom-0 left-3 right-3 h-[2px] bg-primary rounded-full" />
                          )}
                        </button>
                      ))}
                    </div>

                    {/* ── TAB: Dialer ── */}
                    {panelTab === 'dialer' && (
                      <div className="flex flex-col py-5 gap-4" data-testid="dialer-dialpad">
                        {/* ── Dial From selector ── */}
                        {tenantNumbers.length > 1 && (
                          <div className="px-6" data-testid="dial-from-selector">
                            <div className="relative">
                              <label className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-wider mb-1 block">Dial From</label>
                              <select
                                value={callerIdRef.current || callerIdNumber}
                                onChange={(e) => {
                                  callerIdRef.current = e.target.value;
                                  setCallerIdNumber(e.target.value);
                                }}
                                className="w-full appearance-none bg-muted/40 border border-border/30 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 transition-shadow"
                                data-testid="dial-from-dropdown"
                              >
                                {tenantNumbers.map(n => (
                                  <option key={n.id} value={n.phone_number}>
                                    {formatPhoneDisplay(n.phone_number)}{n.friendly_name ? ` — ${n.friendly_name}` : ''}{n.is_primary ? ' ★' : ''}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-2.5 bottom-2.5 w-4 h-4 text-muted-foreground/50 pointer-events-none" />
                            </div>
                          </div>
                        )}
                        {tenantNumbers.length === 1 && (
                          <div className="px-6" data-testid="dial-from-single">
                            <p className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-wider">
                              Calling from <span className="text-foreground font-semibold">{formatPhoneDisplay(tenantNumbers[0].phone_number)}</span>
                            </p>
                          </div>
                        )}
                        <div className="px-6 relative">
                          <input
                            ref={inputRef}
                            value={formatPhoneLive(dialNumber)}
                            onChange={(e) => setDialNumber(e.target.value.replace(/[^\d+*#]/g, ''))}
                            placeholder="Enter number"
                            className="w-full text-center text-[26px] font-semibold font-mono h-14 pr-10 tracking-widest border-0 bg-transparent rounded-xl outline-none ring-0 focus:ring-2 focus:ring-primary/20 placeholder:text-muted-foreground/30 placeholder:font-normal placeholder:tracking-normal transition-shadow"
                            onKeyDown={(e) => e.key === 'Enter' && makeCall()}
                            data-testid="dialer-input"
                          />
                          {dialNumber && (
                            <button
                              onClick={() => setDialNumber(prev => prev.slice(0, -1))}
                              className="absolute right-7 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-foreground transition-all active:scale-[0.9]"
                              data-testid="dialer-backspace"
                            >
                              <Delete className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                        <div className="mx-5 border-t border-border/20" />
                        <DialpadGrid onKey={handleDialKey} />
                        <div className="flex justify-center pt-3 pb-3">
                          <button
                            onClick={makeCall}
                            disabled={!deviceReady || !dialNumber.trim()}
                            className={cn(
                              'w-[68px] h-[68px] rounded-full flex items-center justify-center transition-all duration-200',
                              'bg-primary hover:bg-primary/90 text-primary-foreground',
                              'shadow-[0_6px_24px_-4px] shadow-primary/50 hover:shadow-[0_8px_28px_-4px] hover:shadow-primary/60',
                              'hover:scale-[1.05] active:scale-[0.94]',
                              'disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none',
                            )}
                            data-testid="dialer-call-btn"
                          >
                            <Phone className="w-7 h-7" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* ── TAB: Recent ── */}
                    {panelTab === 'recent' && (
                      <div className="max-h-[380px] overflow-y-auto" data-testid="dialer-recent-list">
                        {loadingRecent ? (
                          <div className="flex items-center justify-center py-10">
                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          </div>
                        ) : recentCalls.length === 0 ? (
                          <p className="text-center text-xs text-muted-foreground/60 py-10">No recent calls</p>
                        ) : (
                          recentCalls.map(call => {
                            const externalPhone = call.direction === 'outbound' ? call.to_number : call.from_number;
                            return (
                              <RecentCallRow
                                key={call.id}
                                name={call.customer_name}
                                phone={externalPhone}
                                direction={call.direction}
                                status={call.status}
                                timestamp={call.started_at}
                                duration={call.duration_seconds}
                                onCall={(num) => { setDialNumber(num.replace(/[^\d+]/g, '')); setPanelTab('dialer'); }}
                                testId={`recent-call-${call.id}`}
                              />
                            );
                          })
                        )}
                      </div>
                    )}

                    {/* ── TAB: Frequent ── */}
                    {panelTab === 'frequent' && (
                      <div className="max-h-[380px] overflow-y-auto" data-testid="dialer-frequent-list">
                        {loadingFrequent ? (
                          <div className="flex items-center justify-center py-10">
                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          </div>
                        ) : frequentContacts.length === 0 ? (
                          <p className="text-center text-xs text-muted-foreground/60 py-10">No frequent contacts</p>
                        ) : (
                          frequentContacts.map((c, i) => (
                            <ContactRow
                              key={i}
                              name={c.customer_name}
                              phone={c.phone}
                              subtitle={`${c.call_count} call${c.call_count !== 1 ? 's' : ''}`}
                              onCall={(num) => { setDialNumber(num.replace(/[^\d+]/g, '')); setPanelTab('dialer'); }}
                              testId={`frequent-${i}`}
                            />
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })()}
    </>
  );
}
